/**
 * The jump module.
 *
 * Warp moves you across a system. Jumping moves you between them, and the two
 * are deliberately different mechanics — see the note on `JUMP_RANGES` in
 * `world/navigation.ts`, which has held the routing rules since the galaxy was
 * first generated. This file is the missing half: what a *ship* can do with
 * them.
 *
 * ── Two gates, for two different reasons ────────────────────────────────────
 *
 * Reaching another system in your own cluster is an *engine* problem. Range
 * comes off the main drive, so a better thruster is worth something beyond top
 * speed and "out of range" is a sentence with a fix in it.
 *
 * Reaching another cluster is a *planning* problem. No engine gets you there;
 * you need an overdrive core, which is consumed, and you have to be sitting in
 * the one system in your cluster nearest the one you are going to. Distance
 * does not enter into it, which is what keeps a cluster jump an event rather
 * than a longer hop.
 */
import { CLUSTER_JUMP_BASE, CLUSTER_JUMP_CEILING, JUMP_RANGES, WORLD_UNITS_PER_GALACTIC, } from '../world/navigation.js';
/**
 * The main thrust the starting hull has, which anchors the range scale below.
 *
 * Named rather than written as `4` in the formula, because the point of the
 * number is *which ship it is*: a new player is meant to sit exactly on the
 * basic tier, and that relationship should survive somebody re-tuning the
 * starter.
 */
export const STARTER_MAIN_THRUST = 4;
/**
 * How much jump range a unit of main thrust is worth.
 *
 * Set so the starter lands precisely on `JUMP_RANGES.basic` — the range the
 * generator guarantees connects every cluster internally, worst measured gap
 * 4,196. So a new player can always get *somewhere*, and never straight across
 * their cluster: the widest pair of systems in one runs to 14,000, so crossing
 * is a chain of hops until the drive is better.
 */
export const JUMP_RANGE_PER_THRUST = JUMP_RANGES.basic / STARTER_MAIN_THRUST;
/** Nothing reaches further than the best engine tier, however it is built. */
export const JUMP_RANGE_CEILING = JUMP_RANGES.advanced;
/** Per unit of main thrust, anchored so the starter sits exactly on the base. */
export const CLUSTER_JUMP_PER_THRUST = CLUSTER_JUMP_BASE / STARTER_MAIN_THRUST;
/**
 * How far this hull can cross between clusters.
 *
 * Scales with the drive exactly as the in-cluster range does, because it is the
 * same drive doing the work — the core supplies the *reach* and the engine
 * still has to push. A better engine covers more ground per core, which is what
 * makes upgrading it worth something on a long haul.
 */
export function clusterJumpRangeFor(performance) {
    if (!performance.flyable)
        return 0;
    const thrust = performance.thrust.main * performance.power.satisfaction;
    return Math.min(CLUSTER_JUMP_CEILING, Math.max(0, thrust * CLUSTER_JUMP_PER_THRUST));
}
/**
 * How far this hull can jump in one go, in galactic units.
 *
 * Off the main drive and derated by power, so an under-powered ship is a
 * short-ranged one — the same satisfaction figure that decides whether it can
 * hold its top speed.
 */
export function jumpRangeFor(performance) {
    if (!performance.flyable)
        return 0;
    const thrust = performance.thrust.main * performance.power.satisfaction;
    return Math.min(JUMP_RANGE_CEILING, Math.max(0, thrust * JUMP_RANGE_PER_THRUST));
}
/**
 * Whether the jump module may be brought online.
 *
 * The same footing warp asks for — under way on the main drive, gear stowed —
 * and deliberately *not* warp's speed floor. Warp needs the speed because it is
 * a continuation of travel you are already doing. Plotting a jump is the
 * opposite: it is something you do while deciding where to go, and requiring a
 * hundred units of speed to look at a map would mean building up a run before
 * you were allowed to know which way to point.
 */
export function canEngageJump(set, landingGear) {
    if (set !== 'main')
        return { engaged: false, blocked: 'main drive required' };
    if (landingGear)
        return { engaged: false, blocked: 'gear down' };
    return { engaged: true, blocked: null };
}
export function jumpVerdict(options) {
    const { sameCluster, distance, range, cores, atGateway } = options;
    const clusterRange = options.clusterRange ?? CLUSTER_JUMP_BASE;
    if (sameCluster) {
        if (distance > range) {
            // Said in world units, like every other distance the player is shown.
            const short = ((distance - range) * WORLD_UNITS_PER_GALACTIC) / 1000;
            return {
                reach: 'out of reach',
                detail: `${short.toFixed(short < 10 ? 1 : 0)} ku short — needs a stronger drive`,
            };
        }
        return { reach: 'ready', detail: 'in range' };
    }
    // Crossing. Position first, because it is the one you cannot buy your way
    // out of: a core in the hold is no use from the wrong side of a cluster.
    if (!atGateway)
        return { reach: 'not the gateway', detail: 'jump from the nearest system' };
    /*
     * Then reach, before the core.
     *
     * A crossing used to have no distance limit at all, which meant one core
     * bought a warp to the far side of the galaxy — a hundred and ninety-five
     * thousand units in a single hop — and the route planner would happily plan
     * it while the jump module drew no marker for it. Reporting the missing core
     * first would also be the wrong order: buying one changes nothing about a
     * cluster the drive cannot reach.
     */
    if (distance > clusterRange) {
        const short = ((distance - clusterRange) * WORLD_UNITS_PER_GALACTIC) / 1_000_000;
        return {
            reach: 'out of reach',
            detail: `${short.toFixed(short < 10 ? 1 : 0)} Mu short — needs a stronger drive`,
        };
    }
    // Says what it *costs*, not merely that the cost is met. A core is consumed
    // and there is no way to tell that from a marker reading "ready" — a player
    // finding their only core gone after a jump they thought was free is a worse
    // surprise than a longer label.
    if (cores < 1)
        return { reach: 'needs a core', detail: 'requires an overdrive core' };
    return {
        reach: 'ready',
        detail: cores === 1 ? 'consumes your last core' : `consumes 1 of ${cores} cores`,
    };
}
/** Whether a verdict permits the jump. One place, so the UI and the act agree. */
export function jumpAllowed(verdict) {
    return verdict.reach === 'ready';
}
/** What a jump costs from the hold. Cluster crossings burn a core; hops do not. */
export function coresSpentOn(sameCluster) {
    return sameCluster ? 0 : 1;
}
//# sourceMappingURL=jump.js.map